import { useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type ClientStagingState, type StagingTemplatePlan } from '../api/admin';
import { useAuth } from '../auth/AuthContext';

// A client's own staging schema. Every client owns one outright; a canonical
// version is only a template it can be started from. Starting from a template
// REPLACES the whole schema, so it always previews first: tables and fields
// gained and lost, and any mapping that would be left pointing at a staging
// name the template doesn't have.

const muted: React.CSSProperties = { fontSize: 12, color: 'var(--mi-muted)' };
const outline: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--mi-border)', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
};

function List({ title, items, tone }: { title: string; items: string[]; tone?: 'warn' }) {
  if (!items.length) return null;
  return (
    <details style={{ marginTop: 6 }} open={tone === 'warn'}>
      <summary style={{ fontSize: 12, cursor: 'pointer', color: tone === 'warn' ? '#a11414' : 'var(--mi-text)' }}>{title} ({items.length})</summary>
      <div style={{ fontFamily: 'monospace', fontSize: 11, maxHeight: 160, overflow: 'auto', padding: '4px 0 0 12px' }}>
        {items.map((x) => <div key={x}>{x}</div>)}
      </div>
    </details>
  );
}

export default function ClientStagingCard({ clientId, isAdmin, state, onChanged }: {
  clientId: string;
  isAdmin: boolean;
  state: ClientStagingState;
  onChanged: () => Promise<void>;
}) {
  const { getToken } = useAuth();
  const [version, setVersion] = useState(state.templates[0]?.schema_version ?? '');
  const [plan, setPlan] = useState<StagingTemplatePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = state.schema;

  const preview = async () => {
    setBusy(true); setError(null); setPlan(null);
    try {
      setPlan(await adminApi.applyStagingTemplate(await getToken(), clientId, version, true));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setError(null);
    try {
      await adminApi.applyStagingTemplate(await getToken(), clientId, version, false);
      setPlan(null);
      await onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ margin: 0, fontSize: 14, color: 'var(--mi-navy)' }}>Staging Schema</h3>
      <div style={{ ...muted, marginTop: 4 }}>
        This client&rsquo;s own staging schema: what their Data Mapping module, exports and SQL download use. A canonical version is a
        template to start from; once applied, the copy is the client&rsquo;s, and later template changes don&rsquo;t reach it.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, marginTop: 12, fontSize: 13, alignItems: 'baseline' }}>
        <div style={{ color: 'var(--mi-muted)' }}>Schema:</div>
        <div style={{ fontWeight: 600 }}>
          {s
            ? <>{s.objects_count} tables, {s.fields_count} fields</>
            : <span style={{ color: 'var(--mi-muted)', fontWeight: 400 }}>none yet: start it from a template, or upload one</span>}
        </div>
        {s && (
          <>
            <div style={{ color: 'var(--mi-muted)' }}>Started from:</div>
            <div>{s.based_on_version ? `template v${s.based_on_version}` : <span style={{ color: 'var(--mi-muted)' }}>an uploaded schema</span>}</div>
          </>
        )}
      </div>

      {s && isAdmin && (
        <div style={{ marginTop: 10 }}>
          <Link to={`/admin/clients/${clientId}/staging/browse`} style={{ fontSize: 12 }}>Browse &amp; edit descriptions and samples →</Link>
        </div>
      )}

      {isAdmin && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--mi-border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Start from canonical</div>
          <div style={muted}>Replaces the whole staging schema with a copy of the template, descriptions and samples included.</div>
          {state.templates.length === 0 ? (
            <div style={{ ...muted, marginTop: 8 }}>No canonical template has been uploaded yet.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <select
                aria-label="Template version"
                value={version}
                disabled={busy}
                onChange={(e) => { setVersion(e.target.value); setPlan(null); }}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--mi-border)' }}
              >
                {state.templates.map((t) => <option key={t.schema_version} value={t.schema_version}>v{t.schema_version}</option>)}
              </select>
              <button onClick={preview} disabled={busy || !version} style={outline}>Preview</button>
              {busy && <span style={muted}>working…</span>}
            </div>
          )}

          {plan && (
            <div role="region" aria-label="Template preview" style={{ marginTop: 10, padding: 10, border: '1px solid var(--mi-border)', borderRadius: 8, background: 'var(--mi-bg)' }}>
              <div style={{ fontSize: 13 }}>
                Applying v{plan.schema_version}: {plan.tablesAdded.length} tables added, {plan.tablesRemoved.length} removed;{' '}
                {plan.fieldsAdded.length} fields added, {plan.fieldsRemoved.length} removed on shared tables.
              </div>
              <List title="Tables added" items={plan.tablesAdded} />
              <List title="Tables removed" items={plan.tablesRemoved} tone={plan.tablesRemoved.length ? 'warn' : undefined} />
              <List title="Fields added" items={plan.fieldsAdded} />
              <List title="Fields removed" items={plan.fieldsRemoved} tone={plan.fieldsRemoved.length ? 'warn' : undefined} />
              <List
                title="Mappings that would point at a missing staging name"
                items={plan.danglingMappings.map((d) => `${d.mappingKey}: ${d.mapping} → ${d.missing}`)}
                tone="warn"
              />
              {plan.danglingMappings.length > 0 && (
                <div style={{ ...muted, color: '#a11414', marginTop: 6 }}>
                  Those mappings aren&rsquo;t changed. Fix them in the mapping JSON after applying, or pick a different template.
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn-primary" onClick={apply} disabled={busy} style={{ padding: '4px 10px', fontSize: 12 }}>
                  Replace staging schema with v{plan.schema_version}
                </button>
                <button onClick={() => setPlan(null)} disabled={busy} style={outline}>Cancel</button>
              </div>
            </div>
          )}
          {error && <div role="alert" style={{ color: '#a11414', fontSize: 12, marginTop: 8 }}>{error}</div>}
        </div>
      )}
      {!isAdmin && (
        <div style={{ ...muted, marginTop: 8 }}>Only Mount Insights admins can change a client&rsquo;s staging schema.</div>
      )}
    </div>
  );
}
